/**
 * The level's timed items and the power-ups (spec 02 §7.3-§7.5, §9.3 doors, §9.4 the potions) -
 * owned by the ITEMS stream, ported from the rebb64 disassembly. The world calls `setupItems` when a
 * level is made, `itemsITick` every I-tick and `itemsMTick` every M-tick; the BUBBLE stream calls
 * `takeRedCross` and `hasLightning`; the PLAYER stream `heartSpeed`; the renderer reads `effectsLook`.
 *
 * Items are `Item`s of kind 'food' / 'powerup' (and 'bonus' in the potion stage) in `world.items`
 * while on screen, at the VIC frame of a 2x2 item: x = 8 x tile + $14, y = 8 x tile + $2D (D_2C8C) -
 * the frame bubbles and death items use, so the renderer's `centreToWorld(x, y)` places them.
 */

import { blastTick, floodTick } from './blast';
import { bonusTick } from './bonus';
import { pickups } from './effects';
import { lookOf, type EffectsLook } from './look';
import { setupLevelItems, systemOf } from './setup';
import { countSeconds, toggleSlots } from './slots';
import { strikeBegin, strikeStep } from './strike';
import { watch } from './watch';
import type { PlayerId, World } from '../types';

export { LIGHTNING_POTION, POWERUPS, RED_DOOR, WHITE_DOOR, powerupName, type Powerup } from './powerups';
export { designatedPowerup, c64Random, bonusBands } from './choice';
export { bonusCells } from './bonus';
export type { EffectsLook, BonusLook } from './look';
export type { BlastPiece } from './blast';

/**
 * A level begins (from `createWorld`, never for the secret room): choose its food and power-up (or
 * door), start their timers, move the door counter in `world.carry`; level 100's two lightning potions.
 */
export function setupItems(world: World): void {
  if (world.level.n === 0) return;
  setupLevelItems(world);
}

/** One I-tick: the per-second item timers, the flood, the blasts, the bonus stage. */
export function itemsITick(world: World): void {
  if (world.level.n === 0) return;
  const sys = systemOf(world);
  watch(world, sys);
  if (sys.bonus) {
    bonusTick(world, sys);
    return;
  }
  countSeconds(world, sys);
  floodTick(world, sys);
  blastTick(sys);
}

/**
 * One M-tick, in the main loop's order ($0A07): the strike steps (L_198A), the slots show or go and the
 * clock counts down (D_1578), a strike starts if due ($0A3B), the players take items (D_2CB7). None of
 * it runs while the bonus stage has the C64 in its own loop.
 */
export function itemsMTick(world: World): void {
  if (world.level.n === 0) return;
  const sys = systemOf(world);
  watch(world, sys);
  if (sys.bonus) return;
  strikeStep(world, sys);
  toggleSlots(world, sys);
  if (world.power.clock > 0) world.power.clock--;
  strikeBegin(world, sys);
  pickups(world, sys);
}

/**
 * The player blows (from `blowBubble`): true if a red-cross bolt should fly instead of a bubble,
 * using one of the player's 19 (L23B8: D_A783 >= 0, then decremented).
 */
export function takeRedCross(world: World, id: PlayerId): boolean {
  if (world.power.redCross[id] <= 0) return false;
  world.power.redCross[id]--;
  return true;
}

/** Level 100: does this player hold a lightning potion (their bubbles turn to lightning)? */
export function hasLightning(world: World, id: PlayerId): boolean {
  return world.power.lightning[id];
}

/**
 * Under the power heart (state $18, handler $28FA): the player's handler runs twice every I-tick and
 * its colour flips each tick. For the PLAYER stream's `updatePlayers`.
 */
export function heartSpeed(world: World, id: PlayerId): boolean {
  return world.itemSystem?.heartOn[id] === true && world.players[id].state === 'play';
}

/** What the renderer draws of the world-wide effects. */
export function effectsLook(world: World): EffectsLook {
  return lookOf(world, world.itemSystem);
}
